import type { Schemas } from "@kollectivia/shared";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";

function Receipt({ receipt }: { receipt: Schemas.EmailReceipt }) {
  const { t, i18n } = useTranslation();
  const [submission, setSubmission] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const client = useQueryClient();
  const review = useMutation({
    mutationFn: (input: Schemas.EmailReview) =>
      api.post(`/v1/email-receipts/${receipt.id}/review`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: ["email-receipts"] }),
  });
  const download = useMutation({
    mutationFn: (part: string) =>
      api.download(
        `/v1/email-receipts/${receipt.id}/${part}`,
        part === "original"
          ? receipt.transport === "mailgun_json"
            ? "synthetic-provider.json"
            : "synthetic-message.eml"
          : receipt.transport === "mailgun_json"
            ? "synthetic-attachment.bin"
            : "synthetic-attachment.txt",
      ),
  });
  const error =
    review.error instanceof ApiException
      ? getApiErrorMessage(review.error, normalizeLang(i18n.resolvedLanguage))
      : t("errors.generic");
  return (
    <article className="space-y-4 rounded-xl border bg-white p-4">
      <h2 className="break-words text-lg font-semibold">{receipt.subject}</h2>
      <p>{t(`email_sandbox.${receipt.status}`)}</p>
      <p className="text-sm">{t("email_sandbox.untrusted")}</p>
      {receipt.transport === "mailgun_json" && <p>{t("email_sandbox.provider_source")}</p>}
      <pre className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-sm">
        {receipt.body_text}
      </pre>
      <details>
        <summary className="min-h-11 cursor-pointer">{t("email_sandbox.original")}</summary>
        <pre className="whitespace-pre-wrap break-words text-sm">{receipt.original}</pre>
      </details>
      <button
        className="btn-secondary min-h-11"
        disabled={download.isPending}
        onClick={() => download.mutate("original")}
      >
        {t("email_sandbox.download_original")}
      </button>
      {download.error && <p role="alert">{t("errors.generic")}</p>}
      {receipt.attachments.map((part, index) => (
        <details key={index}>
          <summary className="min-h-11 cursor-pointer">{part.name}</summary>
          {part.base64 !== undefined ? (
            <p>{t("email_sandbox.opaque_part")}</p>
          ) : (
            <pre className="whitespace-pre-wrap break-words text-sm">{part.text}</pre>
          )}
          <button
            className="btn-secondary min-h-11"
            disabled={download.isPending}
            onClick={() => download.mutate(`parts/${index}`)}
          >
            {t("email_sandbox.download_part")}
          </button>
        </details>
      ))}
      {receipt.status === "pending" && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmed) review.mutate({ decision: "attach", submission_id: submission });
          }}
        >
          <label className="block">
            {t("email_sandbox.submission")}
            <input
              className="mt-1 block w-full rounded border p-3"
              value={submission}
              onChange={(e) => {
                setSubmission(e.target.value);
                setConfirmed(false);
              }}
              required
              pattern="[0-9a-fA-F-]{36}"
            />
          </label>
          <label className="flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {t("email_sandbox.confirm")}
          </label>
          <div className="flex flex-col gap-3 md:flex-row">
            <button className="btn-primary min-h-11" disabled={!confirmed || review.isPending}>
              {t("email_sandbox.attach")}
            </button>
            <button
              type="button"
              className="btn-secondary min-h-11"
              disabled={!confirmed || review.isPending}
              onClick={() => review.mutate({ decision: "reject" })}
            >
              {t("email_sandbox.reject")}
            </button>
          </div>
          {review.error && (
            <p role="alert" className="text-red-700">
              {error}
            </p>
          )}
        </form>
      )}
    </article>
  );
}
export function EmailReceiptsPage() {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["email-receipts"],
    queryFn: () => api.get<Schemas.EmailReceipt[]>("/v1/email-receipts"),
    retry: false,
  });
  return (
    <main className="mx-auto max-w-5xl space-y-5 p-4">
      <h1 data-guide-target="synthetic-receipts" className="text-2xl font-semibold">
        {t("email_sandbox.title")}
      </h1>
      <p className="rounded-lg border border-amber-300 bg-amber-50 p-4">
        {t("email_sandbox.scope")}
      </p>
      <p className="text-sm text-slate-600">{t("email_sandbox.latest")}</p>
      {query.isPending && <p role="status">{t("app.loading")}</p>}
      {query.error && (
        <p role="alert">
          {query.error instanceof ApiException
            ? getApiErrorMessage(query.error, normalizeLang(i18n.resolvedLanguage))
            : t("errors.generic")}
        </p>
      )}
      <button
        className="btn-secondary min-h-11"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      >
        {t("email_sandbox.refresh")}
      </button>
      {!query.error && query.data?.length === 0 && <p>{t("email_sandbox.empty")}</p>}
      {!query.error && query.data?.map((receipt) => <Receipt key={receipt.id} receipt={receipt} />)}
    </main>
  );
}
